import React, {
  InputHTMLAttributes,
  PropsWithChildren,
  useEffect,
  useState,
} from "react";
import {
  styled,
  Checkbox,
  CheckboxProps,
  Theme,
  CSSObject,
} from "@mui/material";
import Typography from "@common/components/Typography";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import IconButton from "@common/components/IconButton";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { borders, dividerStyles } from "./common";
import { WithGrow } from "@common/components/WithGrow";
import { useTranslation } from "react-i18next";
import { DropTargetMonitor } from "react-dnd";
import Droppable from "@common/components/DragNDrop/Droppable";
import {
  useDraggable,
  useDragRef,
} from "@common/components/DragNDrop/Draggable";
import { ListDnDTypes } from "../const";
import { useResize } from "@common/components/Resizer";

export const TableHeaderRow = styled("tr")(() => ({
  whiteSpace: "nowrap",
}));
const Cell = styled("th")(({ theme }) => ({
  position: "relative",
  backgroundColor: theme.palette.background.default,
  padding: 0,
  paddingLeft: theme.spacing(2),
  "&:first-of-type": {
    paddingLeft: 0,
  },
  "&:not(:last-child)::after": dividerStyles(theme),
}));
const FirstCell = styled(Cell)(({ theme }) => ({
  position: "sticky",
  left: 0,
  zIndex: 4,
  borderRight: borders(theme),
  "&:first-of-type::after": { display: "none" },
}));

const getSortIconSx = (theme: Theme): CSSObject => ({
  visibility: "hidden",
  color: theme.palette.common.grey.dark,
});

const getActiveIconSxFn =
  (active: boolean = false) =>
  (theme: Theme) =>
    active
      ? {
          color: theme.palette.common.white,
          visibility: "visible",
        }
      : {};
export const RenderIconSorting = <T, O>(
  props: {
    col: ColDef<T, O>;
    customKey?: keyof T;
    onClick?: () => void;
  } & Pick<ListViewProps<T, keyof T, O>, "sort">
) => {
  const { col, sort, onClick } = props;
  const key = props.customKey ?? col.key;
  const active = col.sortable !== false && sort?.column === key;
  return (
    <IconButton
      onClick={onClick}
      sx={{ padding: 0, marginLeft: (theme) => theme.spacing(1) }}
    >
      {active && sort?.direction === "asc" ? (
        <ArrowUpwardIcon sx={[getSortIconSx, getActiveIconSxFn(active)]} />
      ) : (
        <ArrowDownwardIcon sx={[getSortIconSx, getActiveIconSxFn(active)]} />
      )}
    </IconButton>
  );
};

export const DragWrapper = (
  props: PropsWithChildren<{
    isDraggable: boolean;
    // FIXME: get rid of any
    handleDrop: (item: any, monitor: DropTargetMonitor) => void;
    handleHover: (item: any, monitor: DropTargetMonitor) => void;
    accept: string;
  }>
) => {
  const { isDraggable, handleDrop, handleHover, accept } = props;
  return isDraggable ? (
    <Droppable accept={accept} onDrop={handleDrop} onHover={handleHover}>
      {props.children}
    </Droppable>
  ) : (
    <>{props.children}</>
  );
};

export const HeaderCell = <T, K extends PropertyKey, O>(
  props: {
    col: ColDef<T, O>;
    defaultWidth?: number;
    index: number;
    columns: ColDef<T, O>[];
    selectable?: boolean;
    pinFirstColumn?: boolean;
    CheckboxProps?: CheckboxProps;
    disabledCheckbox?: boolean;
    onColumnsOrderChange?: ListViewProps<T, K, O>["onColumnsOrderChange"];
    onColumnWidthChange?: ListViewProps<T, K, O>["onColumnWidthChange"];
    selectionBehavior?: ListViewProps<T, K, O>["selectionBehavior"];
  } & Pick<ListViewProps<T, K, O>, "sort" | "onSort" | "disableColumnResize">
) => {
  const {
    col,
    defaultWidth,
    index,
    columns,
    selectable = false,
    pinFirstColumn = false,
    onSort = () => {},
    sort,
    disabledCheckbox,
    onColumnsOrderChange,
    onColumnWidthChange = () => {},
    selectionBehavior,
    disableColumnResize,
  } = props;

  const { ref, Resizer } = useResize<HTMLTableCellElement>({
    key: col.key,
    onColumnWidthChange,
    isLastColumn: index === columns.length - 1,
    minWidth: col.width ?? defaultWidth ?? 200,
  });

  const { t } = useTranslation();
  const Component = index === 0 && pinFirstColumn ? FirstCell : Cell;
  const content =
    typeof col.renderHeader === "undefined" ? (
      <Typography size="small">
        {col.label !== "" ? t(col.label) : col.label}
      </Typography>
    ) : (
      col.renderHeader(col, {
        sort,
        onSort,
      })
    );

  interface interaction {
    column: typeof props;
    timing: number;
  }
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const [previousInteraction, setPreviousInteraction] = useState<
    interaction | undefined
  >();
  const [currentInteraction, setCurrentInteraction] = useState<
    interaction | undefined
  >();
  const [hoverPreview, setHoverPreview] = useState<
    "left" | "right" | undefined
  >(undefined);

  const { dragRef } = useDraggable({
    type: ListDnDTypes.LIST_ORDER,
    item: props,
    isDragging: (monitor) => {
      if (monitor.getItemType() !== ListDnDTypes.LIST_ORDER) return false;
      const draggedColIndex = columns.findIndex(
        (c) => c.key === monitor.getItem().col.key
      );
      if (draggedColIndex < index) setHoverPreview("right");
      else if (draggedColIndex > index) setHoverPreview("left");
      else setHoverPreview(undefined);
      return monitor.getItem().col.key === props.col.key;
    },
  });

  const handleDragRef = useDragRef(dragRef);
  useEffect(() => {
    if (
      typeof currentInteraction !== "undefined" &&
      typeof props.col !== "undefined" &&
      onColumnsOrderChange !== undefined
    ) {
      if (
        typeof previousInteraction === "undefined" ||
        currentInteraction.timing - previousInteraction.timing >= 5e2
      ) {
        columns.splice(currentInteraction.column.index, 1);
        onColumnsOrderChange([
          ...columns.slice(0, index),
          currentInteraction.column.col,
          ...columns.slice(index),
        ]);
        setPreviousInteraction(currentInteraction);
      }
    }
  }, [currentInteraction]);

  const handleDrop = (item: typeof props, monitor: DropTargetMonitor) => {
    if (monitor.getItemType() === ListDnDTypes.LIST_ORDER) {
      setIsHovering(false);
      setCurrentInteraction({
        column: item,
        timing: Date.now(),
      });
    }
  };

  const { renderArrow = true } = col.sortOptions ?? {};
  const isDraggable =
    onColumnsOrderChange !== undefined && (!pinFirstColumn || index !== 0);

  return (
    <Component
      key={String(col.key)}
      ref={ref}
      onMouseLeave={() => setIsHovering(false)}
      sx={[
        {
          cursor: isDraggable ? "grab" : "auto",
          borderLeft:
            isHovering && hoverPreview === "left" ? "1px solid white" : "",
          borderRight:
            isHovering && hoverPreview === "right" ? "1px solid white" : "",
          userSelect: "none",
        },
        col.sortable !== false && renderArrow
          ? {
              "&:hover *": {
                visibility: "visible",
              },
            }
          : {},
      ]}
    >
      <DragWrapper
        accept={ListDnDTypes.LIST_ORDER}
        handleDrop={handleDrop}
        handleHover={() => setIsHovering(true)}
        isDraggable={isDraggable}
      >
        <WithGrow
          ref={isDraggable ? handleDragRef : undefined}
          sx={(theme) => ({
            padding: theme.spacing(0, 1),
            // Takes into account how the layout of the first column morphs depending on if there's a pinned column or not
            paddingLeft: !selectable ? 1 : pinFirstColumn ? 0 : 2,
          })}
          containerSx={{
            display: "flex",
            flexWrap: "nowrap",
            flexDirection: "row",
            width: "100%",
            height: "auto",
            justifyContent: "center",
            alignItems: "center",
            maxWidth: col.maxWidth,
          }}
          fixed={
            <>
              {selectionBehavior === "checkbox" ||
                (selectionBehavior === undefined &&
                  (selectable && index === 0 ? (
                    <Checkbox
                      {...props.CheckboxProps}
                      onClick={(event) => event.stopPropagation()}
                      disabled={disabledCheckbox}
                      slotProps={{
                        input: {
                          "data-testid": "header-row-checkbox",
                        } as InputHTMLAttributes<HTMLInputElement>,
                      }}
                    />
                  ) : (
                    <></>
                  )))}
            </>
          }
        >
          {content}
          {col.sortable !== false && renderArrow && (
            <RenderIconSorting
              col={col}
              sort={sort}
              onClick={() =>
                col.sortable !== false && renderArrow
                  ? onSort(
                      col.key,
                      col.key === sort?.column && sort?.direction === "asc"
                        ? "desc"
                        : "asc"
                    )
                  : {}
              }
            />
          )}
        </WithGrow>
      </DragWrapper>

      {!disableColumnResize && <Resizer />}
    </Component>
  );
};
